import { useApolloClient, useMutation } from '@apollo/react-hooks';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import S from './booking-form.module.scss';
import { RowBookingButtons } from './row-booking-buttons';
import { RowBookingEvent } from './row-booking-event';
import { RowBookingPromo } from './row-booking-promo';
import { RowBookingTime } from './row-booking-time';
import { BookingCheckBox } from './booking-form-fields/booking-checkbox';
import { BookingDuplicateField } from './booking-form-fields/booking-duplicate-field';
import { BookingGroupsSelect } from './booking-form-fields/booking-groups-select';
import { BookingSelectorField } from './booking-form-fields/booking-selector-field';
import { setBookingError, setBookingField, setErrorFields } from '../../modal-booking-actions';
import { useModalBookingCtx } from '../../modal-booking-context';
import * as BookingServices from '../../modal-booking-services';
import { FormNumberField } from '../../../../common/form/form-number-field';
import { FormSelectField } from '../../../../common/form/form-select-field';
import { useBusiness } from '../../../../../graphql/graph-hooks';
import { SHIFT_EVENT_CREATE } from '../../../../../graphql/mutations/event-create';
import { SHIFT_EVENT_UPDATE } from '../../../../../graphql/mutations/event-update';
import { CREATE_SHIFT_GQL } from '../../../../../graphql/mutations/shift-create';
import { UPDATE_SHIFT } from '../../../../../graphql/mutations/shift-update';
import { GET_GROUP_NAME_LIST } from '../../../../../graphql/queries/group-name-list';
import { BOOKING_TYPES } from '../../../../../helpers/constants';
import { logError } from '../../../../../helpers/errors/bug-report';
import { checkForFilterError } from '../../../../../helpers/filtered-words';
import RichToast from 'components/common/rich-toast';
import useSuggestions, { SuggestionListType } from 'helpers/hooks/useSuggestions';
import { SHIFT_PROMO_CREATE } from '../../../../../graphql/mutations/promo-create';
import { SHIFT_PROMO_UPDATE } from '../../../../../graphql/mutations/promo-update';

const BookingForm = ({ bookingId, onClose, onCompleted, setLoading }) => {
  const CLIENT = useApolloClient();
  const { t } = useTranslation();
  const {
    id,
    timezone,
    business_features: {
      bookings,
      auditions,
      audition_limits: {
        applications_max,
        duration_max
      }
    }
  } = useBusiness();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { groups } = CLIENT.readQuery({
    query: GET_GROUP_NAME_LIST,
    variables: { businessId: id },
  });
  const { checkText } = useSuggestions(SuggestionListType.MANAGER);

  const BOOKINGS_ENABLED = useMemo(
    () => bookings === 'ENABLED', [bookings]
  );

  const AUDITIONS_ENABLED = useMemo(
    () => auditions === 'ENABLED', [auditions]
  );

  const BOOKING_TYPE_OPTIONS = [
    { label: t('bookingCalendar.select_booking_type'), value: '' },
    ... BOOKINGS_ENABLED ? [{ label: t('bookingCalendar.promo'), value: BOOKING_TYPES.PROMO }] : [],
    ... AUDITIONS_ENABLED ? [{ label: t('bookingCalendar.audition'), value: BOOKING_TYPES.AUDITION }] : [],
    ... BOOKINGS_ENABLED ? [{ label: t('bookingCalendar.event'), value: BOOKING_TYPES.EVENT }] : [],
    ... BOOKINGS_ENABLED ? [{ label: t('bookingCalendar.day_showtime'), value: BOOKING_TYPES.DAY }] : [],
    ... BOOKINGS_ENABLED ? [{ label: t('bookingCalendar.evening_showtime'), value: BOOKING_TYPES.EVENING }] : [],
    ... BOOKINGS_ENABLED ? [{ label: t('bookingCalendar.night_showtime'), value: BOOKING_TYPES.NIGHT }] : [],
    ... BOOKINGS_ENABLED ? [{ label: t('bookingCalendar.late_night_showtime'), value: BOOKING_TYPES.LATE_NIGHT }] : [],
  ];

  const GROUP_ID_LIST = useMemo(() => groups.map(({ id }) => id), [groups]);
  const GROUP_OPTIONS = useMemo(() => groups.map(({ id, name }) => ({ value: id, label: name })), [groups]);

  const { state, dispatch } = useModalBookingCtx();

  const IS_CREATE = useMemo(() => !bookingId, [bookingId]);
  const IS_EVENT = useMemo(() => state.form.booking_type === BOOKING_TYPES.EVENT, [state.form.booking_type]);
  const IS_PROMO = useMemo(() => state.form.booking_type === BOOKING_TYPES.PROMO, [state.form.booking_type]);
  const IS_AUDITION = useMemo(() => state.form.booking_type === BOOKING_TYPES.AUDITION, [state.form.booking_type]);

  const handleError = (error, message) => {
    const filterError = checkForFilterError(error, message);
    toast.error(<RichToast title={filterError.title} message={filterError.message} />);
  };

  const handleOnCompleted = (data) => {
    const field = Object.keys(data)[0];
    const { successes, failures } = data[field];
    if (failures.length) {
      handleFailures(failures);
    }
    if (successes.length) {
      onCompleted();
    }
  };

  const [createShift] = useMutation(CREATE_SHIFT_GQL, {
    onCompleted: handleOnCompleted,
    onError: (err) => {
      logError(err, 'CREATE_SHIFT_GQL', BookingForm.displayName);
      handleError(err, t('bookingCalendar.error_creating_booking'));
    },
  });
  const [createEvent] = useMutation(SHIFT_EVENT_CREATE, {
    onError: (err) => {
      logError(err, 'SHIFT_EVENT_CREATE', BookingForm.displayName);

      handleOnError(err);
    },
    onCompleted,
    variables: { businessId: id },
  });

  const [createPromo] = useMutation(SHIFT_PROMO_CREATE, {
    onCompleted: handleOnCompleted,
    onError: (err) => {
      logError(err, 'SHIFT_PROMO_CREATE', BookingForm.displayName);
      handleOnError(err, t('bookingCalendar.error_creating_promo'));
    },
    variables: { businessId: id },
  });

  const [updateEvent] = useMutation(SHIFT_EVENT_UPDATE, {
    notifyOnNetworkStatusChange: true,
    onCompleted,
    onError: (err) => {
      logError(err, 'SHIFT_EVENT_UPDATE', BookingForm.displayName);
      handleError(err, t('bookingCalendar.error_updating_event'));
    },
    variables: { eventId: bookingId },
  });

  const [updatePromo] = useMutation(SHIFT_PROMO_UPDATE, {
    notifyOnNetworkStatusChange: true,
    onCompleted,
    onError: (err) => {
      logError(err, 'SHIFT_PROMO_UPDATE', BookingForm.displayName);
      handleError(err, t('bookingCalendar.error_updating_promo'));
    },
    variables: {
      eventId: bookingId,
      recurring: ['series', 'all'].includes(state.form.selector),
      separateSeries: state.form.selector === 'series',
    },
  });

  const [updateShift] = useMutation(UPDATE_SHIFT, {
    onError: (err) => {
      logError(err, 'UPDATE_SHIFT', BookingForm.displayName);
      handleOnError(err, t('bookingCalendar.error_updating_booking'));
    },
    onCompleted,
    variables: {
      shiftId: bookingId,
      recurring: ['series', 'all'].includes(state.form.selector),
      separateSeries: state.form.selector === 'series',
    },
  });

  const isValidated = !Object.values(state.errors).reduce((prev, curr) => {
    return prev + curr;
  }, '');

  const IS_DISABLED = useMemo(() => isSubmitting || !state.meta.isEditable, [isSubmitting, state.meta.isEditable]);

  const onSubmit = async (e) => {
    setIsSubmitting(true);
    e.preventDefault();
    try {
      let ERRORS = BookingServices.checkBookingForErrors(state.form, dispatch, GROUP_ID_LIST, timezone, duration_max);

      dispatch(setErrorFields(ERRORS));

      if (!Object.values(ERRORS).every((e) => e === '')) {
        return;
      }
      await checkText([state.form.title, state.form.description].join(' '));

      let imageInput = null; // Make no changes regarding the image
      if (state.form.preview && state.form.image_attachment) {
        // Update image
        imageInput = {
          data: state.form.preview.split(',')[1],
          fileName: state.form.image_attachment.name,
          mimeType: state.form.image_attachment.type.split('/')[1].toUpperCase(),
        };
      } else if (!state.form.preview && !state.form.image_attachment) {
        // Delete image
        imageInput = { data: null };
      }

      const variables = BookingServices.getBookingDataParsed(id, state.form, bookingId, timezone, imageInput);
      //update Form so API will take it, date/start_time/end_time/recurring
      if (bookingId) {
        if (IS_EVENT) {
          await updateEvent({ variables });
        } else if (IS_PROMO) {
          await updatePromo({ variables });
        } else {
          await updateShift({ variables });
        }
      } else {
        if (IS_EVENT) {
          await createEvent({ variables });
        } else if (IS_PROMO) {
          await createPromo({ variables });
        } else {
          await createShift({ variables });
        }
      }
    } catch (error) {
      handleError(error, t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFailures = (failures) => {
    failures.forEach((failure) => {
      handleOnError(failure);
    });
  };

  const handleOnError = (error) => {
    let toastMessage;

    let errMsgParts = error.message.toString().split('GraphQL error: ');
    let errorMessage = errMsgParts[errMsgParts.length - 1];

    switch (errorMessage) {
      case 'AUDITIONS_DISABLED':
        toastMessage = 'bookingCalendar.errors.AUDITIONS_DISABLED';
        break;
      case 'AT_AUDITIONS_DAILY_MAX':
        toastMessage = 'bookingCalendar.errors.AT_AUDITIONS_DAILY_MAX';
        break;
      case 'AUDITION_DURATION_LIMIT':
        toastMessage = 'bookingCalendar.errors.AUDITION_DURATION_LIMIT';
        break;
      case 'AUDITIONS_MUST_BE_HARDCAPPED':
        toastMessage = 'bookingCalendar.errors.AUDITIONS_MUST_BE_HARDCAPPED';
        break;
      case 'AUDITION_SLOTS_OVER_LIMIT':
        toastMessage = 'bookingCalendar.errors.AUDITION_SLOTS_OVER_LIMIT';
        break;
      case 'BOOKING_DISABLED':
        toastMessage = 'bookingCalendar.errors.BOOKING_DISABLED';
        break;
      default:
        toastMessage = 'bookingCalendar.errors.SAVING_FAILED';
        break;
    }
    toast.error(t(toastMessage));
  };

  const handleChange = useCallback(
    ({ target: { name, value } }) => {
      dispatch(setBookingField(name, value));
      dispatch(setBookingError(name, BookingServices.validateBookingField(name, value)));

      let { start_time, end_time, booking_type } = state.form;

      switch(name) {
        case 'start_time':
          start_time = value;
          break;
        case 'end_time':
          end_time = value;
          break;
        case 'booking_type':
          booking_type = value;
          break;
        default:
          break;
      };

      dispatch(setBookingError('end_time', BookingServices.validateEndTime(end_time, start_time, timezone, duration_max, booking_type)));
      dispatch(setBookingError('start_time', BookingServices.validateStartTime(start_time, end_time, timezone, duration_max, booking_type)));
    },
    [dispatch, duration_max, state.form]
  );

  const handleCheckboxChange = useCallback(
    ({ target: { name, checked } }) => {
      dispatch(setBookingField(name, checked));
      dispatch(setBookingError(name, BookingServices.validateBookingField(name, checked)));
    },
    [dispatch]
  );

  const handleGroupChange = useCallback(
    ({ name, value }) => {
      dispatch(setBookingField(name, value));
      dispatch(setBookingError(name, BookingServices.validateBookingField(name, value, GROUP_ID_LIST)));
    },
    [dispatch, GROUP_ID_LIST]
  );

  useEffect(() => {
    setLoading(isSubmitting);
  }, [isSubmitting]);

  return (
    <form
      onSubmit={onSubmit}
      className={classNames({
        [S.bookingCreateForm]: IS_CREATE,
        [S.bookingUpdateModal]: !IS_CREATE && !IS_EVENT && !IS_PROMO,
        [S.bookingUpdateEventModal]: !IS_CREATE && IS_EVENT,
        [S.bookingUpdatePromoModal]: !IS_CREATE && IS_PROMO,
      })}
      noValidate
    >
      <RowBookingTime isDisabled={IS_DISABLED} handleChange={handleChange} />

      <FormSelectField
        id='modal_booking_type'
        label={t('bookingCalendar.booking_type')}
        gridArea='booking_type'
        name='booking_type'
        value={state.form.booking_type}
        error={t(state.errors.booking_type)}
        isDisabled={
          IS_DISABLED ||
          (bookingId &&
            [BOOKING_TYPES.EVENT, BOOKING_TYPES.PROMO, BOOKING_TYPES.AUDITION].includes(state.form.booking_type))
        }
        options={
          bookingId &&
          ![BOOKING_TYPES.EVENT, BOOKING_TYPES.PROMO, BOOKING_TYPES.AUDITION].includes(state.meta.initial.shift_type)
            ? BOOKING_TYPE_OPTIONS.filter(
                ({ value }) => ![BOOKING_TYPES.EVENT, BOOKING_TYPES.PROMO, BOOKING_TYPES.AUDITION].includes(value)
              )
            : BOOKING_TYPE_OPTIONS
        }
        handleChange={(e) => {
          if (e.target.value === BOOKING_TYPES.EVENT) {
            dispatch(setBookingField('duplicates', []));
          }
          handleChange(e);
        }}
      />

      {IS_CREATE && (
        <>
          <BookingCheckBox
            gridArea='recurring'
            label={t('bookingCalendar.repeats_weekly')}
            name='recurring'
            isChecked={IS_EVENT ? false : state.form.recurring}
            isDisabled={IS_DISABLED || IS_EVENT}
            handleChange={handleCheckboxChange}
          />

          {state.form.date && (
            <BookingDuplicateField isEvent={IS_EVENT} isDisabled={IS_DISABLED || IS_EVENT} timezone={timezone} />
          )}
        </>
      )}

      {IS_EVENT && <RowBookingEvent isDisabled={IS_DISABLED} handleChange={handleChange} />}
      {IS_PROMO && <RowBookingPromo isDisabled={IS_DISABLED} handleChange={handleChange} />}

      <FormNumberField
        id='modal_booking_slots'
        value={parseInt(state.form.slots)}
        error={t(state.errors.slots)}
        handleChange={handleChange}
        isDisabled={IS_DISABLED}
        gridArea='slots'
        name='slots'
        label={t('bookingCalendar.num_of_dancers')}
        min={1}
        max={applications_max}
      />

      <BookingCheckBox
        gridArea='hardCap'
        label={t('common.strict_limit')}
        name='hardCap'
        isChecked={state.form.hardCap}
        isDisabled={IS_DISABLED}
        handleChange={handleCheckboxChange}
      >
        <>
          <FaInfoCircle />
          <h6>{t('bookingCalendar.strict_limit_info')}</h6>
        </>
      </BookingCheckBox>

      <BookingGroupsSelect
        label={t('bookingCalendar.preapproved_groups')}
        gridArea='preapproved'
        name='preapprovedIds'
        isDisabled={IS_DISABLED}
        collection={GROUP_OPTIONS}
        value={state.form.preapprovedIds.map((id) => id.toString())}
        handleChange={handleGroupChange}
      />

      {IS_CREATE && !IS_AUDITION && (
        <BookingGroupsSelect
          label={t('bookingCalendar.notify_groups_of_booking')}
          gridArea='notify'
          name='notifyIds'
          isDisabled={IS_DISABLED}
          collection={GROUP_OPTIONS}
          value={state.form.notifyIds.map((id) => id.toString())}
          handleChange={handleGroupChange}
        />
      )}

      {!IS_CREATE && !IS_EVENT && state.meta.isEditable && state.meta.initial.recurring && (
        <BookingSelectorField isDisabled={IS_DISABLED} handleChange={handleChange} />
      )}

      <RowBookingButtons
        bookingId={bookingId}
        onClose={onClose}
        disableSubmit={IS_DISABLED || !isValidated}
        isLoading={isSubmitting}
      />
    </form>
  );
};

BookingForm.displayName = 'BookingForm';
BookingForm.propTypes = {
  bookingId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCompleted: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
};

BookingForm.defaultProps = {
  bookingId: null,
};

export { BookingForm };
